import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ArrowRight, Sparkles, Lock, Home, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { trackProductEvent } from "@/lib/productEvents";
import { useVisibilityPause } from "@/lib/useVisibilityPause";
// Marketing screenshots are shipped as WebP at two pixel densities so
// standard-DPI viewports get a ~40 KB asset while Retina/3× phones still
// receive a crisp ~95 KB @2x. Source PNGs at 1600×2560 were converted with
// `sharp` to 600×960 (1x) and 1200×1920 (@2x) WebP at quality 78–80.
//
// The hero phone now cycles four genuinely distinct frames:
//   1. `letter`  — real WebP capture (HQ + Santa letter)
//   2. `brian`   — code-rendered elf-hint screen (mirrors AppExperience's
//                  BrianScreen — populated, localized, on-brand)
//   3. `map`     — real WebP capture (Spirit Map with Coalie meter)
//   4. `mission` — code-rendered Mission/Clue drop screen (uses the
//                  populated `marketing.appExperience.phone.mission.*`
//                  content already shipped in i18n)
// The previously-shipped `mission`/`control` WebP captures were visually
// identical to `letter` (same template, only localized text differed)
// and `postcards.webp` was an empty-state ("No postcards yet"), so we
// don't reuse those captures — instead we render brand-true mock screens
// in code (same approach as AppExperience), which gives us populated
// frames that translate automatically with the rest of the marketing copy.
import letterScreenshot1x from "@/assets/marketing-screens/en/letter.webp";
import letterScreenshot2x from "@/assets/marketing-screens/en/letter@2x.webp";
import mapScreenshot1x from "@/assets/marketing-screens/en/map.webp";
import mapScreenshot2x from "@/assets/marketing-screens/en/map@2x.webp";

// Trust pill icons stay hardcoded (presentational); labels resolve at
// render time from i18n so the pills re-render when the user toggles
// languages.
const TRUST_PILL_KEYS = [
  { key: "privateMissions", icon: Heart },
  { key: "inviteOnly", icon: Lock },
  { key: "indoor", icon: Home },
  { key: "memories", icon: Sparkles },
] as const;

type HeroScreenKey = "letter" | "brian" | "map" | "mission";
type HeroScreen =
  | { id: HeroScreenKey; kind: "image"; src: string; srcSet: string }
  | { id: HeroScreenKey; kind: "render"; render: () => React.ReactNode };

// Default first frame is the Santa letter — the strongest "this is the magic"
// moment, and it crops cleanly inside the hero bezel at 10:16. We then
// alternate image captures with code-rendered mock screens so each frame
// feels distinct (photo → mock → photo → mock). When adding new entries:
// they must be (a) visually distinct from the others and (b) populated
// with real content — never empty states or placeholder/policy screens.
const HERO_SCREENS: HeroScreen[] = [
  {
    id: "letter",
    kind: "image",
    src: letterScreenshot1x,
    srcSet: `${letterScreenshot1x} 1x, ${letterScreenshot2x} 2x`,
  },
  { id: "brian", kind: "render", render: () => <BrianHeroScreen /> },
  {
    id: "map",
    kind: "image",
    src: mapScreenshot1x,
    srcSet: `${mapScreenshot1x} 1x, ${mapScreenshot2x} 2x`,
  },
  { id: "mission", kind: "render", render: () => <MissionHeroScreen /> },
];

// Each frame holds for ~5.5s — long enough to read the screen, short
// enough to feel alive. The crossfade itself is CROSSFADE_MS and runs
// concurrently with the hold (the new image starts fading in while the
// old one is still visible), so the *perceived* dwell is ~5s.
const TICK_MS = 5500;
const REDUCED_TICK_MS = 14000;
const CROSSFADE_MS = 700;
// How long the manual-pause gate stays held after a dot tap. The
// total time the chosen frame remains visible is MANUAL_PAUSE_MS +
// (next tick delay), because once the gate releases, the auto-
// advance effect schedules a fresh setInterval whose first tick
// fires after the normal/reduced delay. Tuned so the total dwell
// in normal motion is ~9 s (3600 + 5400 = 9000), which is the
// 8–10 s reading window the task asks for. Under reduced motion
// the dwell stretches to ~15.6 s, which is fine — reduced-motion
// users prefer longer holds anyway. Re-tapping any dot restarts
// the gate so the visitor always gets a fresh window.
const MANUAL_PAUSE_MS = 3600;

export function Hero() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();

  function smoothTo(id: string) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <section
      id="hero"
      className="relative overflow-hidden px-5 pb-16 pt-14 sm:px-8 lg:px-12 lg:pb-20 lg:pt-24"
      data-testid="section-hero"
    >
      {/* Soft cinematic light beam behind the hero column */}
      <div
        className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[80%] w-[120%] -translate-x-1/2 opacity-70"
        style={{
          background:
            "radial-gradient(50% 40% at 50% 30%, hsla(0, 80%, 55%, 0.35) 0%, transparent 70%), radial-gradient(40% 30% at 75% 35%, hsla(38, 88%, 60%, 0.22) 0%, transparent 70%)",
          filter: "blur(30px)",
          mixBlendMode: "screen",
        }}
        aria-hidden
      />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
        <div className="jingle-reveal relative">
          {/* Subtle premium glass surface behind hero copy (lg+) */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-8 -inset-y-10 -z-10 hidden rounded-[2.5rem] lg:block"
            style={{
              background:
                "linear-gradient(135deg, hsla(38,78%,55%,0.05) 0%, hsla(354,55%,18%,0.18) 60%, transparent 100%)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              boxShadow:
                "inset 0 1px 0 0 hsla(38,78%,75%,0.08), 0 30px 80px -40px rgba(0,0,0,0.5)",
            }}
          />
          {/* Soft gold-edge ring around the hero text panel (lg+) */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-8 -inset-y-10 -z-10 hidden rounded-[2.5rem] lg:block"
            style={{
              padding: 1,
              background:
                "linear-gradient(160deg, hsla(38,82%,72%,0.45), hsla(38,82%,72%,0) 38%, hsla(38,82%,72%,0) 68%, hsla(38,82%,72%,0.22))",
              WebkitMask:
                "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
            }}
          />
          <p
            className="inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.36em] text-jingle-gold"
            data-testid="hero-eyebrow"
          >
            <span aria-hidden className="text-jingle-gold-soft/80">✦</span>
            <span className="jingle-gold-shimmer">
              SANTA HAS A MISSION FOR YOUR FAMILY
            </span>
            <span aria-hidden className="text-jingle-gold-soft/80">✦</span>
          </p>
          <p className="mt-3">
            <span
              className="inline-flex items-center rounded-full border border-jingle-gold/25 bg-white/[0.04] px-3 py-1 text-[11px] font-medium text-jingle-snow/70 backdrop-blur"
              data-testid="badge-hero-scavenger-hunt"
            >
              {t("marketing.hero.badge")}
            </span>
          </p>
          {/* Cold-traffic phase-1 — H1 sharpened from the brand-poetic
              "Santa has a mission for your family this Christmas." to
              the benefit-direct "Create a Christmas mission in
              minutes." so a cold paid visitor reads the speed promise
              + output in the first second. The italic gold treatment
              moves from "family" to "in minutes" — same visual
              vocabulary, new emphasis. Hard-coded in JSX (matches
              existing pattern); en.json badge string is the only
              i18n-driven copy in this block. */}
          <h1 className="mt-5 font-serif text-5xl font-semibold leading-[1.04] tracking-tight text-jingle-snow sm:text-6xl lg:text-[5.25rem] lg:leading-[1.02]">
            Create a Christmas mission{" "}
            <span className="relative inline-block">
              <span className="relative z-10 italic text-jingle-gold-soft">
                in minutes
              </span>
              <span
                className="absolute -inset-x-3 -inset-y-1 -z-0 rounded-full opacity-70 blur-2xl"
                style={{
                  background:
                    "radial-gradient(closest-side, hsla(38, 78%, 60%, 0.55), transparent)",
                }}
              />
            </span>
            .
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-jingle-snow/80 sm:text-xl">
            Santa, elves, clues, photos, and a glowing Christmas Spirit
            Map — built for your family without hours of planning.
          </p>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Primary CTA: Create your mission (free start) */}
            <Button
              onClick={() => {
                track("hero_signup_click", { source: "hero" });
                setLocation("/create");
              }}
              className="group h-14 rounded-2xl bg-jingle-cranberry px-7 text-base font-semibold text-jingle-snow shadow-[0_18px_40px_-12px_hsla(354,70%,38%,0.7)] transition hover:bg-[hsl(354,70%,42%)]"
              data-testid="button-hero-launch-updates"
            >
              Create your mission (free start)
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            {/* Secondary CTA: See how it works */}
            <Button
              onClick={() => {
                track("hero_how_it_works_click");
                smoothTo("how-it-works");
              }}
              variant="ghost"
              className="h-14 rounded-2xl border border-jingle-gold/40 bg-white/5 px-7 text-base text-jingle-snow backdrop-blur transition hover:border-jingle-gold/70 hover:bg-white/10"
              data-testid="button-hero-how"
            >
              See how it works
            </Button>
          </div>

          <p className="mt-4 max-w-xl text-sm leading-relaxed text-jingle-snow/70">
            Free to start. No print-outs, no prep, no yard required.
            Indoor-friendly and invite-only.
          </p>
          {/* Cold-traffic phase-1 — "preview the magic before you pay"
              reassurance. Sits directly under the existing logistics
              reassurance so the cold visitor sees both promises (free
              + previewable) before scrolling. Italic gold sparkle matches
              the rest of the hero. */}
          <p
            className="mt-2 flex max-w-xl items-center gap-2 text-sm leading-relaxed text-jingle-gold-soft/95"
            data-testid="text-hero-preview-reassurance"
          >
            <Sparkles aria-hidden className="h-3.5 w-3.5 shrink-0" />
            <span className="italic">Preview the magic before you pay.</span>
          </p>

          {/* Three short value bullets — sit below the CTA row + footnote so
              they never push the CTAs below the fold on 360 / 390 / 430
              widths. Compact list (`text-sm`, tight gap) with gold sparkle
              markers to echo the rest of the hero. */}
          <ul
            className="mt-6 grid max-w-xl gap-2 text-sm text-jingle-snow/85 sm:text-[15px]"
            data-testid="hero-value-bullets"
          >
            <li className="flex items-start gap-2">
              <Sparkles
                aria-hidden
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-jingle-gold"
              />
              <span>Santa-led mission, one lead elf, real magic.</span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles
                aria-hidden
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-jingle-gold"
              />
              <span>5–10 minutes a day. Whole family plays together.</span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles
                aria-hidden
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-jingle-gold"
              />
              <span>
                Every captured spark becomes part of the Brightbell Archive.
              </span>
            </li>
          </ul>

          {/* Cold-traffic phase-1 — "Your mission can include" card.
              Enumerates the six concrete pieces a cold visitor's
              mission ships with so the offer is legible in one glance,
              not inferred from bullets. Code-rendered (gold-edged
              pine card, sparkle markers); no image, no animation.
              Sits between the value bullets and the trust pills so
              the funnel reads: speed promise → product enumeration →
              safety trust. */}
          <div
            className="mt-8 max-w-xl rounded-2xl border border-jingle-gold/35 bg-white/[0.04] p-5 backdrop-blur"
            data-testid="card-hero-mission-includes"
            style={{
              boxShadow:
                "0 18px 40px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,235,180,0.10)",
            }}
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-jingle-gold-soft">
              Your mission can include
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-1.5 text-[13px] leading-snug text-jingle-snow/90 sm:grid-cols-2 sm:gap-x-5 sm:text-sm">
              {[
                "Santa's welcome",
                "Elf clues",
                "Photo challenges",
                "Christmas Spirit Map",
                "Family invite",
                "Keepsake recap",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <Sparkles
                    aria-hidden
                    className="mt-0.5 h-3 w-3 shrink-0 text-jingle-gold"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <ul className="mt-10 flex flex-wrap gap-2.5">
            {TRUST_PILL_KEYS.map((p) => {
              const Icon = p.icon;
              const label = t(`marketing.hero.trustPills.${p.key}`);
              return (
                <li
                  key={p.key}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[12px] text-jingle-snow/85 backdrop-blur"
                >
                  <Icon className="h-3.5 w-3.5 text-jingle-gold" />
                  {label}
                </li>
              );
            })}
          </ul>

          {/* Mobile-only "the host has arrived" beat. The desktop hero
              gets a 9:16 Santa peek-and-wave video cameo at 2xl+ via
              MarketingCharacterCameos, but Task #342 verified that no
              left-anchored cameo can fit on phones (`px-5` hero gutter,
              eyebrow / badge / H1 all start at x=20). This inline pill
              restores the warmth in a layout-safe way: it lives in the
              normal flow between the trust pills and the phone mock,
              so it can never overlap the headline or phone mock at
              360 / 375 / 390 / 414 / 430 widths.

              Hidden at lg+ so we don't double up with the desktop
              cameo. Decorative wave animation on the hand glyph is
              a CSS keyframe (`jingle-wave`) gated by
              `motion-reduce:animate-none` so reduced-motion users
              still see the static greeting. */}
          <MobileHostGreeting />
        </div>

        <div className="jingle-reveal relative mx-auto w-full max-w-md lg:max-w-none">
          {/* Tasteful gold sparkle cluster behind phone */}
          <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
            <span
              className="jingle-sparkle absolute"
              style={{ left: "8%", top: "12%", width: 4, height: 4, animationDelay: "-2s" }}
            />
            <span
              className="jingle-sparkle absolute"
              style={{ left: "92%", top: "20%", width: 3, height: 3, animationDelay: "-4s" }}
            />
            <span
              className="jingle-sparkle absolute"
              style={{ left: "4%", top: "60%", width: 2.5, height: 2.5, animationDelay: "-1s" }}
            />
            <span
              className="jingle-sparkle absolute"
              style={{ left: "96%", top: "72%", width: 4, height: 4, animationDelay: "-3s" }}
            />
            <span
              className="jingle-sparkle absolute"
              style={{ left: "50%", top: "-2%", width: 3.5, height: 3.5, animationDelay: "-6s" }}
            />
            <span
              className="jingle-sparkle absolute"
              style={{ left: "20%", top: "92%", width: 2.5, height: 2.5, animationDelay: "-5s" }}
            />
          </div>
          <PhoneMockup />
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   Mobile-only "the host has arrived" greeting.

   Why this exists: the desktop hero gets a 9:16 Santa peek-and-wave
   video cameo at 2xl+ via MarketingCharacterCameos. Task #342
   verified that no left-anchored cameo can fit on phones — the
   hero uses `px-5` (20px) and the eyebrow / badge / H1 all start
   at x=20, so there is no real left gutter to occupy. A phone-
   native treatment restores that warmth without colliding.

   Implementation:
   - Lives in the normal flow, between the trust pills and the
     phone mock, so it's layout-safe at every phone width
     (360 / 375 / 390 / 414 / 430 verified safe).
   - `lg:hidden` keeps it phone/small-tablet only — desktop has
     the cinematic Santa cameo at 2xl+.
   - Wave animation uses the `jingle-host-wave` keyframe (see
     index.css). Tailwind's `motion-reduce:[animation:none]`
     pauses the wave for users who request reduced motion; the
     static glyph still reads as a friendly Santa.
   - Pure SVG glyph (Santa hat + waving hand) avoids loading
     another video plate; no decode cost, no autoplay quirks.
   ============================================================ */
function MobileHostGreeting() {
  const { t } = useTranslation();
  return (
    <div
      className="mt-6 flex items-center gap-3 lg:hidden"
      data-testid="hero-mobile-host-greeting"
      aria-label={t("marketing.hero.mobileHostGreeting")}
    >
      <span
        aria-hidden
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-jingle-gold/35 bg-white/[0.06] shadow-[0_8px_24px_-12px_hsla(38,82%,60%,0.55)] backdrop-blur"
      >
        {/* Soft warm glow ring behind the glyph */}
        <span
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, hsla(38,82%,65%,0.45), transparent 70%)",
          }}
        />
        {/* Santa-hat + waving-hand glyph. Hat sits fixed; hand is
            the only element that animates. SVG keeps the visual
            crisp at every DPR and avoids shipping a raster asset
            for a 36px element. */}
        <svg
          viewBox="0 0 32 32"
          className="relative h-5 w-5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Santa hat brim */}
          <ellipse cx="16" cy="11" rx="9" ry="2" fill="#F8F6F1" />
          {/* Santa hat body */}
          <path
            d="M7 11 C 9 4, 21 4, 25 9 L 22 11 Z"
            fill="hsl(354, 70%, 48%)"
          />
          {/* Hat pom-pom */}
          <circle cx="24.5" cy="6.5" r="1.6" fill="#F8F6F1" />
          {/* Face */}
          <circle cx="16" cy="16" r="4.5" fill="#F2D7B6" />
          {/* Beard */}
          <path
            d="M11.8 16 C 12.2 22, 19.8 22, 20.2 16 C 19 17.6, 13 17.6, 11.8 16 Z"
            fill="#F8F6F1"
          />
        </svg>
      </span>
      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-jingle-snow/90">
        <span
          aria-hidden
          className="jingle-host-wave text-base leading-none motion-reduce:[animation:none]"
        >
          👋
        </span>
        <span>{t("marketing.hero.mobileHostGreeting")}</span>
      </span>
    </div>
  );
}

/* ============================================================
   Hero phone — auto-rotates through five real captured screenshots
   of the live JINGLE app (letter → map → mission → control → postcards).
   The bezel, notch, screen radius, glow halo, gold ring, sparkles, and
   floating baubles are unchanged from the single-frame version shipped
   by Task #74. Only the inner stage contents change.

   Motion guards:
   - IntersectionObserver: no ticks while the phone is off-screen.
   - hover/focus pause: rotation freezes while the user interacts.
   - prefers-reduced-motion: interval slows to ~12 s, crossfade kept.
   - useVisibilityPause (Task #82): no ticks while the tab is hidden.
   ============================================================ */
function PhoneMockup() {
  const { t } = useTranslation();

  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const [inView, setInView] = useState(false);
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  const docHidden = useVisibilityPause();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const manualPauseTimerRef = useRef<number | null>(null);

  // Tapping a dot jumps to that frame and holds it for MANUAL_PAUSE_MS
  // before the auto-advance ticker resumes. Re-tapping any dot (or the
  // already-active one) restarts the timer so the visitor always gets
  // a full reading window after their last interaction. Composes with
  // the existing hover/focus/visibility/reduced-motion gates: if any
  // of those become true during the pause window, the ticker stays
  // halted regardless; when the manual window expires, those gates
  // remain in charge.
  function jumpToFrame(target: number) {
    setIdx(target);
    setManualPause(true);
    if (manualPauseTimerRef.current !== null) {
      window.clearTimeout(manualPauseTimerRef.current);
    }
    manualPauseTimerRef.current = window.setTimeout(() => {
      setManualPause(false);
      manualPauseTimerRef.current = null;
    }, MANUAL_PAUSE_MS);
  }

  // Cleanup the pending manual-pause timeout on unmount so it can't
  // fire setManualPause on a torn-down component.
  useEffect(() => {
    return () => {
      if (manualPauseTimerRef.current !== null) {
        window.clearTimeout(manualPauseTimerRef.current);
        manualPauseTimerRef.current = null;
      }
    };
  }, []);

  // Track prefers-reduced-motion changes reactively.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Gate ticks on viewport visibility so we don't burn CPU while the
  // user is reading the hero copy or other page sections.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setInView(Boolean(entry?.isIntersecting)),
      { rootMargin: "0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-advance: respects all pause gates.
  useEffect(() => {
    if (paused || manualPause || !inView || docHidden) return;
    const delay = reduced ? REDUCED_TICK_MS : TICK_MS;
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % HERO_SCREENS.length);
    }, delay);
    return () => window.clearInterval(id);
  }, [paused, manualPause, inView, docHidden, reduced]);

  return (
    <div
      ref={containerRef}
      className="relative isolate mx-auto aspect-[10/16] w-full max-w-[440px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* warm magical glow halo (fixed) */}
      <div className="jingle-soft-glow absolute -inset-10 -z-10" aria-hidden />

      {/* gold-edge ring around the phone shell */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[2.6rem]"
        style={{
          padding: 1.5,
          background:
            "linear-gradient(160deg, hsla(38,82%,72%,0.55), hsla(38,82%,72%,0) 35%, hsla(38,82%,72%,0) 65%, hsla(38,82%,72%,0.32))",
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />
      {/* phone shell — same bezel as the carousel below */}
      <div
        className="relative h-full w-full rounded-[2.6rem] border border-white/10 bg-[hsl(220,18%,8%)] p-2.5 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.8),0_30px_60px_-20px_hsla(38,78%,40%,0.45),inset_0_1px_0_0_hsla(38,82%,75%,0.18)]"
        style={{
          backgroundImage:
            "radial-gradient(120% 80% at 50% 0%, hsla(38,82%,75%,0.10), transparent 60%)",
        }}
        data-testid="hero-phone"
      >
        {/* notch — same as the carousel below */}
        <div className="absolute left-1/2 top-2.5 z-20 h-5 w-28 -translate-x-1/2 rounded-full bg-black/90" />

        {/* screen — every HERO_SCREENS entry is rendered as its own
            absolutely-positioned <img> layer; only the active one
            (idx) has opacity:1, the others are opacity:0. A shared
            opacity transition produces a true crossfade between the
            outgoing and incoming frames (the previous implementation
            keyed a single <img> on idx, which hard-remounted the
            element and read as a flash through black instead of a
            phase). data-testid stays on the active layer so the
            verifier and any product analytics keep pointing at the
            currently-shown screen. width/height on each layer match
            the 10:16 native capture ratio so the browser reserves
            the space and there is no CLS. */}
        <div className="relative h-full w-full overflow-hidden rounded-[2.1rem] bg-[hsl(220,18%,8%)]">
          {HERO_SCREENS.map((s, i) => {
            const active = i === idx;
            const layerStyle: React.CSSProperties = {
              opacity: active ? 1 : 0,
              transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
              zIndex: active ? 1 : 0,
            };
            const alt = t(`marketing.hero.phone.screens.${s.id}.alt`);
            const testIdProps = active
              ? { "data-testid": "hero-phone-screenshot" }
              : {};
            if (s.kind === "image") {
              return (
                <img
                  key={s.id}
                  src={s.src}
                  srcSet={s.srcSet}
                  alt={alt}
                  className="absolute inset-0 block h-full w-full object-cover"
                  width={390}
                  height={624}
                  decoding="async"
                  // First frame is eager so there's no tick-1 pop-in
                  // when the crossfade fires; later frames lazy-load
                  // since the rotation is now 4 entries (per the
                  // ~3-entry threshold called out in the prior comment).
                  loading={i === 0 ? "eager" : "lazy"}
                  style={layerStyle}
                  aria-hidden={!active}
                  {...testIdProps}
                />
              );
            }
            return (
              <div
                key={s.id}
                role="img"
                aria-label={alt}
                aria-hidden={!active}
                className="absolute inset-0 block h-full w-full"
                style={layerStyle}
                {...testIdProps}
              >
                {s.render()}
              </div>
            );
          })}
        </div>

        {/* Progress dots — count auto-derives from HERO_SCREENS.length */}
        <div className="absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1.5">
          {HERO_SCREENS.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={t(`marketing.hero.phone.screens.${s.id}.alt`)}
              onClick={() => jumpToFrame(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx
                  ? "w-7 bg-jingle-gold"
                  : "w-1.5 bg-jingle-snow/30 hover:bg-jingle-snow/55"
              }`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      </div>

      {/* floating accent baubles (fixed) */}
      <div
        className="absolute -left-4 top-10 h-5 w-5 rounded-full bg-jingle-gold shadow-[0_0_24px_hsl(38,78%,60%)]"
        aria-hidden
      />
      <div
        className="absolute -right-2 top-40 h-3.5 w-3.5 rounded-full bg-jingle-cranberry shadow-[0_0_18px_hsla(354,70%,55%,0.8)]"
        aria-hidden
      />
      <div
        className="absolute -right-6 bottom-24 h-4 w-4 rounded-full bg-jingle-aurora-teal/80 shadow-[0_0_22px_hsla(178,65%,50%,0.65)]"
        aria-hidden
      />
    </div>
  );
}

/* ============================================================
   Code-rendered hero phone screens — these are NOT screenshots.
   They render brand-true mock UIs inside the same 10:16 bezel
   the image-based frames use, so the carousel can show populated,
   localized in-app moments without us needing to capture and
   re-export PNGs every time the underlying app surface drifts.
   The visual language (chrome, status bar, candy-striped header
   accent, gradient backgrounds) mirrors AppExperience.tsx so the
   two carousels feel like the same product. All text resolves
   from i18n; no hardcoded English strings.
   ============================================================ */

// Status-bar / candy-stripe header used by every code-rendered
// hero screen. The 9:41 / title / Day N triplet mirrors the
// AppExperience ScreenChrome so the two carousels feel cohesive.
function HeroScreenChrome({ title, day }: { title: string; day: string }) {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-2"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, hsl(354,70%,42%) 0 8px, hsl(0,0%,98%) 8px 16px)",
        }}
      />
      <div className="flex items-center justify-between px-5 pt-5 text-[9px] font-semibold uppercase tracking-[0.22em] text-jingle-snow/75">
        <span>9:41</span>
        <span className="text-jingle-snow/85">{title}</span>
        <span>{day}</span>
      </div>
    </div>
  );
}

// Brian Brightbell elf hint — mirrors AppExperience BrianScreen but
// scaled for the hero bezel. Uses the populated, fully-localized
// `marketing.appExperience.phone.brian.*` strings.
function BrianHeroScreen() {
  const { t } = useTranslation();
  return (
    <div
      className="flex h-full w-full flex-col"
      style={{
        background:
          "linear-gradient(180deg, hsl(354,55%,16%) 0%, hsl(160,60%,10%) 55%, hsl(354,55%,12%) 100%)",
      }}
    >
      <HeroScreenChrome
        title={t("marketing.hero.phone.labels.inbox")}
        day={t("marketing.hero.phone.labels.day", { n: 13 })}
      />
      <div className="flex min-h-0 flex-1 flex-col px-5 pt-5 pb-6">
        <div className="flex items-center justify-between text-[9px] font-medium uppercase tracking-[0.24em] text-jingle-snow/55">
          <span>{t("marketing.appExperience.phone.brian.topLeft")}</span>
          <span className="text-jingle-snow/40">
            {t("marketing.appExperience.phone.brian.topRight")}
          </span>
        </div>

        <div className="mt-5 flex items-start gap-2.5">
          <div
            className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-base shadow-[0_4px_18px_hsla(178,65%,40%,0.55)]"
            style={{
              background:
                "linear-gradient(160deg, hsl(178,65%,45%) 0%, hsl(160,55%,28%) 100%)",
            }}
            aria-hidden
          >
            <span>🧝</span>
          </div>
          <div className="flex-1 rounded-2xl rounded-tl-md bg-jingle-snow px-4 py-3 text-jingle-charcoal shadow-[0_18px_40px_-18px_rgba(0,0,0,0.6)]">
            <div className="flex items-baseline gap-1.5">
              <p className="font-serif text-[11px] font-semibold uppercase tracking-[0.18em] text-jingle-aurora-teal">
                {t("marketing.appExperience.phone.brian.name")}
              </p>
              <p className="text-[8px] uppercase tracking-[0.18em] text-jingle-charcoal/45">
                {t("marketing.appExperience.phone.brian.role")}
              </p>
            </div>
            <p className="mt-1.5 font-serif text-[13px] leading-snug">
              {t("marketing.appExperience.phone.brian.message")}
            </p>
            <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-jingle-aurora-teal/30 bg-jingle-aurora-teal/10 px-2.5 py-1 text-[10px] font-semibold text-jingle-aurora-teal">
              ✨ {t("marketing.appExperience.phone.brian.spiritWhenFind")}
            </div>
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between rounded-xl border border-jingle-aurora-teal/25 bg-jingle-aurora-teal/10 px-3 py-2 text-[10px] text-jingle-aurora-teal">
          <span>{t("marketing.appExperience.phone.brian.rooting")}</span>
          <span className="font-semibold uppercase tracking-[0.18em]">
            {t("marketing.appExperience.phone.brian.hintSent")}
          </span>
        </div>
      </div>
    </div>
  );
}

// Mission/clue drop screen — the "today's clue" moment. Uses the
// populated `marketing.appExperience.phone.mission.*` strings.
function MissionHeroScreen() {
  const { t } = useTranslation();
  return (
    <div
      className="flex h-full w-full flex-col"
      style={{
        background:
          "linear-gradient(180deg, hsl(354,55%,18%) 0%, hsl(354,55%,10%) 100%)",
      }}
    >
      <HeroScreenChrome
        title={t("marketing.hero.phone.labels.todayMission")}
        day={t("marketing.hero.phone.labels.day", { n: 13 })}
      />
      <div className="flex min-h-0 flex-1 flex-col px-5 pt-5 pb-6">
        <div className="flex items-center justify-between text-[9px] font-medium uppercase tracking-[0.24em] text-jingle-snow/55">
          <span>{t("marketing.appExperience.phone.mission.drop")}</span>
          <span className="text-jingle-snow/40">
            {t("marketing.appExperience.phone.mission.time")}
          </span>
        </div>

        <div
          className="mt-4 rounded-2xl border border-jingle-gold/30 bg-[hsl(38,40%,96%)] px-4 py-4 text-jingle-charcoal shadow-[0_18px_40px_-18px_rgba(0,0,0,0.6)]"
          style={{
            backgroundImage:
              "radial-gradient(120% 80% at 50% 0%, hsla(38,82%,75%,0.25), transparent 60%)",
          }}
        >
          <p className="font-serif text-[10px] font-semibold uppercase tracking-[0.22em] text-jingle-cranberry">
            {t("marketing.appExperience.phone.mission.header")}
          </p>
          <p className="mt-2 font-serif text-[15px] italic leading-snug text-jingle-charcoal">
            “{t("marketing.appExperience.phone.mission.clue")}”
          </p>
          <p className="mt-3 text-[11px] leading-snug text-jingle-charcoal/70">
            {t("marketing.appExperience.phone.mission.snap")}
          </p>
        </div>

        <div className="mt-auto flex items-center justify-between rounded-xl bg-jingle-gold px-3.5 py-2.5 text-[11px] font-semibold text-jingle-charcoal shadow-[0_12px_24px_-12px_hsla(38,78%,40%,0.7)]">
          <span className="uppercase tracking-[0.18em]">
            {t("marketing.appExperience.phone.mission.found")}
          </span>
          <span aria-hidden>📸</span>
        </div>
      </div>
    </div>
  );
}
